"use client";

import { apiGetEventTradeRecordList } from "@/api/comments";
import { calc, formatCurrency, get } from "@/utils/utils";
import { useInfiniteQuery, type UseInfiniteQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

export interface ActivityItem {
    id: string;
    userId: string;
    userName: string;
    userAvatar: string;
    imageUrl: string;
    tradeType: "buy" | "sell" | "split" | "merge";
    betType: 1 | 2; // 1: Yes, 2: No
    guessNo: string;
    guessTitle: string;
    groupTitle?: string;
    contracts: number;
    amount: number;
    price: number;
    timestamp: number;
    time: string;
    guessTypeDesc: string;
}

interface GuessOption {
    guessNo: string;
    title: string;
}

interface UseActivityTabResult {
    activities: ActivityItem[];
    loading: boolean;
    guessOptions: Array<{ value: string; label: string }>;
    amountOptions: Array<{ value: string; label: string }>;
    selectedGuessNo: string;
    selectedMinAmount: string;
    handleGuessChange: ( value: string ) => void;
    handleAmountChange: ( value: string ) => void;
    activityQuery: UseInfiniteQueryResult<any, Error>;
}

type TradeRecord = {
    imageUrl?: string;
    guessTitle?: string;
    groupTitle?: string;
    direction?: number;
    tradeType?: number;
    guessType?: number;
    quantity?: number;
    totalAmount?: number;
    createTime?: number;
    userId?: string;
    userName?: string;
    userAvatar?: string;
    guessNo?: string;
};

const mapTradeType = ( tradeType: number, direction: number ): "buy" | "sell" | "split" | "merge" => {
    // tradeType: 1=Limit, 2=Market, 3=Split, 4=Merge
    if ( tradeType === 3 ) return "split";
    if ( tradeType === 4 ) return "merge";
    // tradeType === 1 或 2 时，使用 direction 判断 buy/sell
    return direction === 2 ? "sell" : "buy";
};
const mapBetType = ( guessType: number ): 1 | 2 => ( guessType === 2 ? 2 : 1 );

const formatRelativeTime = ( timestamp: number ): string => {
    if ( !timestamp ) {
        return "";
    }
    const now = Date.now();
    const diffMs = Math.max( 0, now - timestamp );
    if ( diffMs < 60_000 ) {
        return "Just now";
    }
    const diffMinutes = Math.floor( diffMs / 60_000 );
    if ( diffMinutes < 60 ) {
        return `${diffMinutes}m`;
    }
    const diffHours = Math.floor( diffMinutes / 60 );
    if ( diffHours < 24 ) {
        return `${diffHours}h`;
    }
    const diffDays = Math.floor( diffHours / 24 );
    if ( diffDays > 30 ) {
        return "30d";
    }
    return `${diffDays}d`;
};

const toNumber = ( value: any, fallback = 0 ) => {
    const parsed = Number( value );
    return Number.isFinite( parsed ) ? parsed : fallback;
};

export function useActivityTab( eventNo: string | undefined, guessList: GuessOption[] = [] ): UseActivityTabResult {
    const [ selectedGuessNo, setSelectedGuessNo ] = useState( () => {
        if ( guessList.length === 1 ) {
            return guessList[0].guessNo;
        }
        return "all";
    } );
    const [ selectedMinAmount, setSelectedMinAmount ] = useState( "none" );

    const pageSize = 20;

    // Build guess filter options
    const guessOptions = useMemo( () => {
        if ( guessList.length === 1 ) {
            return [ { value: guessList[0].guessNo, label: guessList[0].title } ];
        }
        const options = [ { value: "all", label: "ALL" } ];
        guessList.forEach( ( guess ) => {
            options.push( { value: guess.guessNo, label: guess.title } );
        } );
        return options;
    }, [ guessList ] );

    useEffect( () => {
        if ( guessList.length === 1 && selectedGuessNo === "all" ) {
            setSelectedGuessNo( guessList[0].guessNo );
        }
    }, [ guessList, selectedGuessNo ] );

    // Build amount filter options
    const amountOptions = useMemo(
        () => [
            { value: "none", label: "None" },
            { value: "10", label: formatCurrency( 10, { minDecimals: 0, maxDecimals: 0 } ) },
            { value: "100", label: formatCurrency( 100, { minDecimals: 0, maxDecimals: 0 } ) },
            { value: "1000", label: formatCurrency( 1000, { minDecimals: 0, maxDecimals: 0 } ) },
            { value: "10000", label: formatCurrency( 10000, { minDecimals: 0, maxDecimals: 0 } ) },
            { value: "100000", label: formatCurrency( 100000, { minDecimals: 0, maxDecimals: 0 } ) },
        ],
        [],
    );

    const handleGuessChange = ( value: string ) => {
        setSelectedGuessNo( value );
    };

    const handleAmountChange = ( value: string ) => {
        setSelectedMinAmount( value );
    };

    const enabled = Boolean( eventNo );
    const activityQuery = useInfiniteQuery( {
        queryKey: [ "eventTradeRecords", eventNo, selectedGuessNo, selectedMinAmount ],
        enabled,
        initialPageParam: 1,
        queryFn: async ( { pageParam } ) => {
            if ( !eventNo ) {
                return { records: [], pageNum: pageParam };
            }
            const guessNoParam = selectedGuessNo === "all" ? undefined : selectedGuessNo;
            const minAmountParam = selectedMinAmount === "none" ? undefined : Number( selectedMinAmount );
            const [ isOk, res ] = await apiGetEventTradeRecordList( {
                pageNum: pageParam,
                pageSize,
                eventNo,
                guessNo: guessNoParam,
                minAmount: minAmountParam,
            } );
            if ( !isOk ) {
                throw new Error( get( res, "msg", "Failed to fetch activities" ) );
            }
            const records = get( res, "data.tradeRecords", [] );
            return { records: Array.isArray( records ) ? records : [], pageNum: pageParam };
        },
        getNextPageParam: ( lastPage ) => {
            const records = Array.isArray( lastPage.records ) ? lastPage.records : [];
            if ( records.length < pageSize ) {
                return undefined;
            }
            return ( lastPage.pageNum ?? 1 ) + 1;
        },
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    } );

    const activities = useMemo( () => {
        const pages = activityQuery.data?.pages ?? [];
        return pages.flatMap( ( page, pageIndex ) => {
            const records: TradeRecord[] = page.records ?? [];
            return records.map( ( record, recordIndex ) => {
                const quantity = toNumber( get( record, "quantity", 0 ), 0 );
                const totalAmount = toNumber( get( record, "totalAmount", 0 ), 0 );
                const price = quantity > 0 ? Number( calc( "totalAmount / quantity", { totalAmount, quantity } ) ) : 0;
                const timestamp = toNumber( get( record, "createTime", Date.now() ), Date.now() );
                const tradeType = toNumber( get( record, "tradeType", 1 ), 1 );
                const direction = toNumber( get( record, "direction", 1 ), 1 );

                return {
                    ...record,
                    id: `${get( record, "guessNo", "" )}-${timestamp}-${pageIndex}-${recordIndex}`,
                    userId: get( record, "userId", "" ),
                    userName: get( record, "userName", "" ),
                    userAvatar: get( record, "userAvatar", "" ),
                    imageUrl: get( record, "imageUrl", "" ),
                    tradeType: mapTradeType( tradeType, direction ),
                    betType: mapBetType( toNumber( get( record, "guessType", 1 ), 1 ) ),
                    guessTypeDesc: get( record, "guessTypeDesc", "" ),
                    guessNo: get( record, "guessNo", "" ),
                    guessTitle: get( record, "guessTitle", "" ),
                    groupTitle: get( record, "groupTitle", "" ),
                    contracts: quantity,
                    amount: totalAmount,
                    price,
                    timestamp,
                    time: formatRelativeTime( timestamp ),
                } satisfies ActivityItem;
            } );
        } );
    }, [ activityQuery.data ] );

    return {
        activities,
        loading: activityQuery.isLoading && !activityQuery.isFetchingNextPage,
        guessOptions,
        amountOptions,
        selectedGuessNo,
        selectedMinAmount,
        handleGuessChange,
        handleAmountChange,
        activityQuery,
    };
}
